import { Command, CommandEnum } from './CommandModule';
import { Events } from '../events';
import { eventsManager } from '../managers/EventsManager';
import { EntityFactory, Entity, GeometryPrimitive, math, GeometryGroup } from '../SavaneJS';

export class CreateGeometryGroupCommand  extends Command
{
    // To store new created group
    private _newGroup: GeometryGroup;
    // Geometry list to add to the created group
    private _geometryList: Array<GeometryPrimitive | GeometryGroup>;
    // Parent entity of the new group
    private _parent: Entity;

    constructor(geometryList: Array<GeometryPrimitive | GeometryGroup>) {
        super();
        // Create an empty group into which the geometries will be added
        this._newGroup = EntityFactory.getInstance().createEmptyGeometryGroup();
        // Store geometry list to add to the group
        this._geometryList = geometryList.filter((item) => item.isIGeometryEntity());
        // Store the parent
        this._parent = this._geometryList[0].parent!;
    }

    // Command name returned from a global enum
    name(): string {
        return CommandEnum.CreateGeometryGroupCommand;
    }

    // Datas that allows the command to be executed
    execDatas(): any {
        return {
            group: this._newGroup,
            geometries: this._geometryList
        };
    }

    // Undo the current command
    undo() {
        // Remove the group from its parent so it disapears
        eventsManager.instance.dispatch(Events.REMOVE_GRAPHICAL_ENTITY,
            {
                entity: this._newGroup,
            });

        // Get parent invert global
        let toWorld = this._parent.transform.invertedGlobalMatrix;
        // Parse all geometries added to the entity group
        for (let i = 0; i < this._geometryList.length; i++) {
            // Get geometry
            let geo = this._geometryList[i];

            // get geometry global
            let global = geo.transform.globalMatrix;

            // Delete geometry from created group
            geo.deleteFromParent();
            // Add back the geometry to the original parent (i.e. parent of the group now)
            this._parent.addChild(geo);

            // express global to new parent -> new local
            let matrix = math.mat4.create();
            math.mat4.multiply(matrix, toWorld, global);
            math.mat4.copy(geo.transform.localMatrix, matrix);

            // Recreate each sub entity graphical node
            eventsManager.instance.dispatch(Events.ADD_GRAPHICAL_ENTITY,
                {
                    entity: geo
                });
        }

        // Finally remove the group that received the result of the group creation
        this._newGroup.deleteFromParent();

        // Execute parent function
        super.undo();

        // Select the object used to create the group originally
        eventsManager.instance.dispatch(Events.CHANGE_EDITOR_SELECTION,
            {
                selection: this._geometryList,
                keepSelected: false,
                showTulip: false
            });
    }

    // Execute current command (redo)
    execute() {
        // Add the group to the first object parent (empty for the moment)
        // This is not 100% rigorous since the objects to group might have different parents BUT, we have to make a choice here and the group parent will be updated
        // when the group will be moved in the scene anyway
        this._parent.addChild(this._newGroup);

        // Parse all geometries to add them to the group
        for (let i = 0; i < this._geometryList.length; i++) {
            // Get geometry from current floor
            let item = this._geometryList[i];

            // Delete geometry entities (will be recreated inside group)
            eventsManager.instance.dispatch(Events.REMOVE_GRAPHICAL_ENTITY,
                {
                    entity: item,
                });

            //Process to group
            let toLocal = this._newGroup.transform.invertedGlobalMatrix;
            let world = item.transform.globalMatrix;

            // Delete the link from parents to the object
            item.deleteFromParent();
            // Add it to the created group
            this._newGroup.addChild(item);

            let matrix = math.mat4.create();
            math.mat4.multiply(matrix, toLocal, world);
            math.mat4.copy(item.transform.localMatrix, matrix);
        }

        this._newGroup.recenter();
        // Recreate entity and node
        eventsManager.instance.dispatch(Events.ADD_GRAPHICAL_ENTITY,
            {
                entity: this._newGroup,
            });

        //Execute parent function
        super.execute();

        // Select created group at the end
        eventsManager.instance.dispatch(Events.CHANGE_EDITOR_SELECTION,
            {
                selection: [this._newGroup],
                keepSelected: false,
                showTulip: false
            });
    }
};
